import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UserContract } from "../api/contracts.type";
import { MedicalRecordRequest } from "../api/medical-record-requests.type";
import { SubmitButton } from "../components/shared/buttons/submit-button";
import ThemedSpinner from "../components/shared/spinner";
import Typography from "../components/shared/typography";
import { useLoadContractRequests } from "../hooks/useLoadContractRequests";
import { useUserDetails } from "../hooks/useUserDetails";
import MedicalRecordRequestDetail from "../medical-record-requests/components/MedicalRecordRequestDetail";
import { MedicalRecordRequestsProvider } from "../medical-record-requests/context/MedicalRecordRequestsContext";

function mapUserContractToMedicalRecordRequest(
  contract: UserContract,
  patientId: string,
  patientName: string | null
): MedicalRecordRequest {
  const provider = contract.signing?.providerSummary;
  const deliveryMethods = provider?.deliveryMethods ?? [];
  return {
    id: contract.id ?? contract.signing?.id ?? "",
    pdfUrl:
      contract.signedDocument?.signed_url ?? contract.signedPdfUrl ?? null,
    status: contract.signing?.status ?? "unknown",
    patientId,
    patientName,
    createdAt: contract.signing?.created_at ?? new Date().toISOString(),
    recipientName: provider?.recipientName ?? provider?.name ?? null,
    recipientType: provider?.type ?? null,
    recipientExternalId: null,
    deliveryMethod: deliveryMethods[0] ?? null,
    faxNumber: provider?.fax ?? null,
    emailAddress: provider?.email ?? null,
    recipientDetails: {
      institution:
        provider?.type === "institution"
          ? {
              id: typeof provider?.id === "string" ? provider.id : undefined,
              name: provider?.name ?? null,
              type: provider?.type ?? null,
              phone: provider?.phone ?? null,
              fax: provider?.fax ?? null,
              email: provider?.email ?? null,
              address:
                typeof provider?.address === "string" ? provider.address : null,
              contact_details: Array.isArray(provider?.contact_details)
                ? provider?.contact_details
                : null,
            }
          : null,
      provider:
        provider?.type !== "institution"
          ? {
              id: typeof provider?.id === "string" ? provider.id : undefined,
              name: provider?.name ?? null,
              type: provider?.type ?? null,
              phone: provider?.phone ?? null,
              fax: provider?.fax ?? null,
              email: provider?.email ?? null,
              address:
                typeof provider?.address === "string" ? provider.address : null,
              contact_details: Array.isArray(provider?.contact_details)
                ? provider?.contact_details
                : null,
            }
          : null,
    },
  };
}

export function MedicalRecordRequestDetailPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { userDetails, isLoading, error } = useUserDetails();
  const isAdmin = userDetails?.type === "admin";
  const userFullName = useMemo(() => {
    const parts = [userDetails?.first_name, userDetails?.last_name].filter(
      Boolean
    );
    return parts.length ? parts.join(" ") : userDetails?.first_name ?? null;
  }, [userDetails?.first_name, userDetails?.last_name]);

  const {
    requests: userRequests,
    isLoading: userRequestsLoading,
    isError: userRequestsError,
    error: userRequestsErrorValue,
  } = useLoadContractRequests({
    userId: userDetails?.id,
    enabled: Boolean(userDetails?.id) && !isAdmin,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <ThemedSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 min-h-[40vh]">
        <Typography variant="h4" className="text-terracotta">
          Unable to verify your permissions
        </Typography>
        <Typography variant="body1" className="text-terracotta/80">
          {error instanceof Error ? error.message : "Please try again later."}
        </Typography>
      </div>
    );
  }

  if (!requestId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-2">
        <Typography variant="h4" className="text-terracotta">
          Request not specified
        </Typography>
        <SubmitButton
          onClick={() => navigate("/medical-records/requests")}
          icon={<ArrowLeftIcon className="w-4 h-4" />}
          color="beige"
        >
          Back to list
        </SubmitButton>
      </div>
    );
  }

  if (isAdmin) {
    return (
      <MedicalRecordRequestsProvider>
        <div className="container mx-auto px-4 py-8 space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Typography variant="h2" className="text-terracotta">
                Medical Record Request
              </Typography>
              <Typography variant="body2" className="text-terracotta/70">
                Review every field and update contact info before sending.
              </Typography>
            </div>
            <SubmitButton
              color="beige"
              onClick={() => navigate("/medical-records/requests/all")}
              icon={<ArrowLeftIcon className="w-4 h-4" />}
            >
              Back to list
            </SubmitButton>
          </div>

          <MedicalRecordRequestDetail requestId={requestId} />
        </div>
      </MedicalRecordRequestsProvider>
    );
  }

  if (userRequestsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <ThemedSpinner />
      </div>
    );
  }

  if (userRequestsError) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 min-h-[40vh]">
        <Typography variant="h4" className="text-terracotta">
          Unable to load your request
        </Typography>
        <Typography variant="body1" className="text-terracotta/80">
          {userRequestsErrorValue instanceof Error
            ? userRequestsErrorValue.message
            : "Please try again later."}
        </Typography>
      </div>
    );
  }

  const userRequest = userRequests.find((entry) => entry.id === requestId);
  const mappedRequest = userRequest
    ? mapUserContractToMedicalRecordRequest(
        userRequest,
        userDetails?.id ?? "",
        userFullName
      )
    : null;

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Typography variant="h2" className="text-terracotta">
            Medical Record Request
          </Typography>
          <Typography variant="body2" className="text-terracotta/70">
            Review the status and delivery details for this request.
          </Typography>
        </div>
        <SubmitButton
          color="beige"
          onClick={() => navigate("/medical-records/requests")}
          icon={<ArrowLeftIcon className="w-4 h-4" />}
        >
          Back to list
        </SubmitButton>
      </div>

      <MedicalRecordRequestDetail request={mappedRequest} allowEdit={false} />
    </div>
  );
}

export default MedicalRecordRequestDetailPage;
